/**
 * getLeadScore — single source of truth for "the numeric AI score on a lead".
 *
 * Why this exists:
 *   The AI Lead Targeting engine writes `lead.ai_score` as a dict
 *   (`{primary:{score, product}, secondary, ...}`) but ~15 frontend call
 *   sites still treat `lead.ai_score` as a number. The backend was patched
 *   to flatten the response (see `server.py::_flatten_ai_score`), but
 *   relying on backend-only protection means any future regression on the
 *   server (e.g. a new endpoint forgets to flatten, or a cache returns the
 *   old dict shape) white-screens the entire /leads page with:
 *     Uncaught Error: Objects are not valid as a React child
 *
 * Use this helper anywhere you would have written `lead.ai_score || 0`.
 * It accepts the legacy int, the new dict, missing / null values, and
 * NaN garbage from buggy seed data — and always returns a clamped 0..100
 * integer that is safe to render directly in JSX.
 */
export function getLeadScore(lead) {
  if (!lead) return 0;
  const v = lead.ai_score;
  if (typeof v === "number" && Number.isFinite(v)) return Math.max(0, Math.min(100, Math.round(v)));
  if (v && typeof v === "object") {
    // Engine dict shape — pull the primary product's score.
    const n = v?.primary?.score;
    if (typeof n === "number" && Number.isFinite(n)) return Math.max(0, Math.min(100, Math.round(n)));
  }
  // Legacy fallbacks that some screens read.
  const legacy = lead.lead_score ?? lead.probability ?? lead.score;
  if (typeof legacy === "number" && Number.isFinite(legacy)) return Math.max(0, Math.min(100, Math.round(legacy)));
  return 0;
}
